import React from 'react';
import Modal from '../ui/Modal';
import { UsageDetail, UsageLine } from '../../services/api';

/**
 * UsageBreakdownModal — the bookings behind an invoice / preview total.
 *
 * Shared by the admin Billing page (Nancy reconciling a clinician's month) and
 * the member Billing page (a clinician checking how their hours were reached).
 * Every number comes from the API; nothing is recomputed here.
 */

interface UsageBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  detail: UsageDetail | null;
  loading: boolean;
  error: string | null;
  /** Admin only: export this clinician's month as CSV. */
  onDownloadCsv?: () => void;
  /** Show the client column (both admin and member already see client names elsewhere). */
  showClient?: boolean;
}

export const money = (n: number): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;
const hrs = (n: number): string => `${n.toFixed(2)}h`;
export const periodLabel = (period: string): string =>
  new Date(`${period}-01T12:00:00`).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
const dateLabel = (date: string): string =>
  new Date(`${date}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });

const KIND_LABEL: Record<UsageLine['booking_kind'], string> = {
  client: 'Client session',
  self: 'Self-booked',
  late_cancel_held: 'Late cancel (held)',
};

const TypePill: React.FC<{ type: UsageLine['room_type'] }> = ({ type }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
      type === 'pod' ? 'bg-clinic-gold/20 text-clinic-brown' : 'bg-clinic-primary/15 text-clinic-primary'
    }`}
  >
    {type === 'pod' ? 'Pod' : 'Studio'}
  </span>
);

const rateNote = (d: UsageDetail): string => {
  const studioRates = Array.from(new Set([d.peak_rate, d.standard_rate, d.off_peak_rate]));
  const studio =
    studioRates.length === 1
      ? `Studio time is billed at ${money(studioRates[0])}/hr (${d.user_type} tier)`
      : `Studio time is billed at ${money(d.peak_rate)} peak / ${money(d.standard_rate)} standard / ${money(d.off_peak_rate)} off-peak per hour (${d.user_type} tier)`;
  return `${studio}. Pods are a flat ${money(d.pod_rate_per_hour)}/hr (${money(d.pod_rate_per_hour / 2)} per 30 min). Buffer minutes are billed room time.`;
};

interface TotalRow {
  label: string;
  value: number;
  strong?: boolean;
}

const totalRows = (d: UsageDetail): TotalRow[] => {
  const rows: TotalRow[] = [];
  const sameRate = d.peak_rate === d.standard_rate && d.standard_rate === d.off_peak_rate;
  if (d.studio_hours > 0) {
    if (sameRate) {
      rows.push({
        label: `Studio hours · ${hrs(d.studio_hours)} × ${money(d.standard_rate)}`,
        value: d.peak_amount + d.standard_amount + d.off_peak_amount,
      });
    } else {
      if (d.peak_hours > 0) rows.push({ label: `Peak hours · ${hrs(d.peak_hours)} × ${money(d.peak_rate)}`, value: d.peak_amount });
      if (d.standard_hours > 0) rows.push({ label: `Standard hours · ${hrs(d.standard_hours)} × ${money(d.standard_rate)}`, value: d.standard_amount });
      if (d.off_peak_hours > 0) rows.push({ label: `Off-peak hours · ${hrs(d.off_peak_hours)} × ${money(d.off_peak_rate)}`, value: d.off_peak_amount });
    }
  }
  if (d.pod_hours > 0) {
    rows.push({ label: `Pod usage · ${hrs(d.pod_hours)} × ${money(d.pod_rate_per_hour)}`, value: d.pod_amount });
  }
  rows.push({ label: 'Membership fee', value: d.membership_fee });
  rows.push({ label: 'Subtotal', value: d.subtotal, strong: true });
  rows.push({ label: 'GST (5%)', value: d.tax_amount });
  if (d.credit_amount > 0) rows.push({ label: 'Credit applied', value: -d.credit_amount });
  rows.push({ label: 'Total due', value: d.total_amount, strong: true });
  return rows;
};

const UsageBreakdownModal: React.FC<UsageBreakdownModalProps> = ({
  isOpen,
  onClose,
  detail,
  loading,
  error,
  onDownloadCsv,
  showClient = true,
}) => {
  const title = detail ? `Usage detail · ${detail.clinician_name}` : 'Usage detail';
  const description = detail ? `${periodLabel(detail.billing_period)} · ${detail.user_type} tier` : undefined;
  const invoiceDiffers =
    !!detail && detail.invoice_total_amount !== null && Math.abs(detail.invoice_total_amount - detail.total_amount) >= 0.005;

  const footer = (
    <div className="flex items-center justify-end gap-3">
      {onDownloadCsv && detail && (
        <button
          type="button"
          onClick={onDownloadCsv}
          className="inline-flex items-center px-4 py-2 border border-clinic-primary text-clinic-primary rounded-lg hover:bg-clinic-cream transition-colors font-medium"
        >
          Download CSV
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center px-4 py-2 bg-clinic-primary text-white rounded-lg hover:bg-clinic-primary/90 transition-colors font-medium"
      >
        Close
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} description={description} size="full" footer={footer}>
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary" />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg bg-clinic-accent/10 border border-clinic-accent/30 px-4 py-3 text-sm text-clinic-accent">{error}</div>
      )}

      {!loading && !error && detail && (
        <div className="space-y-5">
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-clinic-cream/50 rounded-lg">
              <p className="text-xs text-clinic-muted">Studio hours</p>
              <p className="text-xl font-semibold text-clinic-dark">{hrs(detail.studio_hours)}</p>
            </div>
            <div className="p-3 bg-clinic-cream/50 rounded-lg">
              <p className="text-xs text-clinic-muted">Pod hours</p>
              <p className="text-xl font-semibold text-clinic-dark">{hrs(detail.pod_hours)}</p>
            </div>
            <div className="p-3 bg-clinic-cream/50 rounded-lg">
              <p className="text-xs text-clinic-muted">Total hours</p>
              <p className="text-xl font-semibold text-clinic-dark">{hrs(detail.total_hours)}</p>
            </div>
            <div className="p-3 bg-clinic-primary/10 rounded-lg">
              <p className="text-xs text-clinic-muted">Total due</p>
              <p className="text-xl font-semibold text-clinic-dark">{money(detail.total_amount)}</p>
            </div>
          </div>

          <p className="text-sm text-clinic-muted">{rateNote(detail)}</p>

          {detail.invoice_id !== null && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                invoiceDiffers ? 'bg-clinic-gold/10 border-clinic-gold text-clinic-dark' : 'bg-clinic-cream/50 border-clinic-primary-lighter text-clinic-dark'
              }`}
            >
              Invoice #{detail.invoice_id} ({detail.invoice_status}) was generated at {money(detail.invoice_total_amount ?? 0)}.
              {invoiceDiffers && <> Bookings have changed since then; the live total below is {money(detail.total_amount)}.</>}
            </div>
          )}

          {/* Per-booking lines */}
          <div className="border border-clinic-primary-lighter rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-clinic-primary-lighter text-sm">
                <thead className="bg-clinic-cream/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">When</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Room</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Booked as</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-clinic-muted uppercase tracking-wider">Hours</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-clinic-muted uppercase tracking-wider">Rate</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-clinic-muted uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-clinic-primary-lighter bg-white">
                  {detail.lines.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-clinic-muted">
                        No billed bookings in {periodLabel(detail.billing_period)}.
                      </td>
                    </tr>
                  )}
                  {detail.lines.map((line) => (
                    <tr key={line.booking_id} className="hover:bg-clinic-cream/30">
                      <td className="px-3 py-2 whitespace-nowrap text-clinic-dark">
                        {dateLabel(line.date)} · {line.start_time}–{line.end_time}
                        <span className="block text-xs text-clinic-muted">
                          {line.duration_minutes} min{line.buffer_minutes > 0 ? ` + ${line.buffer_minutes} min buffer` : ''}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-clinic-dark">
                        <div className="flex items-center gap-2">
                          <TypePill type={line.room_type} />
                          <span>{line.room_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-clinic-dark">
                        {KIND_LABEL[line.booking_kind]}
                        {showClient && line.client_name && (
                          <span className="block text-xs text-clinic-muted">{line.client_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-clinic-dark">{hrs(line.billable_hours)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-clinic-dark">
                        {line.rate === null ? <span className="text-clinic-muted">mixed</span> : `${money(line.rate)}/hr`}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-medium text-clinic-dark">{money(line.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <dl className="w-full md:w-80 text-sm">
              {totalRows(detail).map((row) => (
                <div
                  key={row.label}
                  className={`flex justify-between py-1.5 ${row.strong ? 'border-t border-clinic-primary-lighter font-semibold text-clinic-dark' : 'text-clinic-dark'}`}
                >
                  <dt className={row.strong ? '' : 'text-clinic-muted'}>{row.label}</dt>
                  <dd className={row.value < 0 ? 'text-clinic-accent' : ''}>{money(row.value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UsageBreakdownModal;
